/**
 * Per-colour variant-artwork fan-out (BLU-655 B2 — per-colour upload mode).
 *
 * A colour of a product spans N synced variant SKUs (one per size). The client
 * drops ONE design PNG for that colour + side (or one platen variant's sizes of
 * it); this hook publishes it to EVERY one of those SKUs' front_artwork /
 * back_artwork metafields through the group commit (review 1-S2):
 *   1. validate the PNG client-side (same PNG-only / <= 25 MB / non-empty / hash
 *      checks, so the friendly errors match the single-cell uploader byte for
 *      byte),
 *   2. content-hash it ONCE with `sha256Hex`,
 *   3. mint ONE object, PUT the bytes ONCE (`putWithProgress`) and commit ONCE
 *      with `skus` set: the server verifies the object once and publishes it
 *      to every SKU, answering per SKU (`skuResults`). A cell with more than
 *      `MAX_GROUP_COMMIT_SKUS` SKUs splits into several groups, at most
 *      `COLOUR_UPLOAD_CONCURRENCY` at a time (the shared `runWithConcurrency`).
 *      Each SKU keeps its own status, so a partial failure lists exactly which
 *      SKUs still need publishing.
 *
 * `retryFailed` re-runs ONLY the SKUs that failed, with the retained File + sha,
 * because the content-addressed key makes the whole mint -> PUT -> commit trio
 * idempotent (re-uploading the same bytes is a no-op the server verifies).
 *
 * Concurrency discipline mirrors `useVariantArtworkUpload` exactly: a monotonic
 * run id drops any stale async result from a superseded drop, an AbortController
 * cancels the in-flight PUT on replace/unmount, and the staged object URL is
 * revoked on replace/unmount. A NEW upload that fails validation never wipes the
 * already-published resting state (the grid reads that from the server) — this
 * hook only ever owns the in-flight run.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { runWithConcurrency } from '@/lib/run-with-concurrency';
import {
  ARTWORK_CONTENT_TYPE,
  MAX_ARTWORK_BYTES,
  putWithProgress,
  sha256Hex,
  type ArtworkSide,
  type ArtworkUploadErrorKind,
} from './useVariantArtworkUpload';
import { uploadRefusalKey } from '../lib/upload-refusal';

/** Rollup phase for the whole colour + side fan-out. */
export type ColourArtworkPhase =
  // nothing staged this session:
  | 'idle'
  // hashing the dropped file (before any SKU is touched):
  | 'hashing'
  // publishing the SKUs (bounded fan-out):
  | 'running'
  // every SKU published:
  | 'success'
  // the run finished with >= 1 SKU still failed (retryable via `retryFailed`):
  | 'partial'
  // a client-side validation / hash failure — NOTHING was sent; a NEW file is
  // required (mint/PUT/commit failures are `partial`, not `error`, because they
  // retry idempotently):
  | 'error';

/** Per-SKU status inside the fan-out. */
export type SkuUploadPhase = 'pending' | 'uploading' | 'publishing' | 'done' | 'failed';

export interface SkuUploadStatus {
  phase: SkuUploadPhase;
  /** 0..1 — real XHR upload fraction while `uploading`; 1 once past PUT. */
  progress: number;
  /** Present only on `failed`. */
  message?: string;
}

export interface ColourArtworkUploadError {
  kind: ArtworkUploadErrorKind;
  message: string;
}

export interface ColourArtworkUploadState {
  phase: ColourArtworkPhase;
  /** Per-SKU status, keyed by the design-bearing Shopify variant SKU. */
  statuses: Record<string, SkuUploadStatus>;
  /** Object URL of the staged file for the thumbnail; revoked on replace/unmount. */
  previewUrl: string | null;
  fileName: string | null;
  byteSize: number | null;
  error: ColourArtworkUploadError | null;
}

export interface UseColourArtworkUploadArgs {
  locationId: string;
  side: ArtworkSide;
  /** Design-bearing Shopify variant SKUs (`shopify_sku`) for this colour. */
  skus: string[];
  /** Fired once every SKU in the run has published. */
  onAllUploaded?: () => void;
  /**
   * Phase 86: the platen variant this cell grouped its SKUs under
   * (`platenVariantKey`, for example `14x16:Small`). Sent on every commit so
   * the server refuses a SKU whose assignment changed since the page loaded.
   * Unset for a product without rules: no key is sent and a failed SKU never
   * stops the others.
   */
  expectedPlatenVariantKey?: string;
  /** Commit warnings (info fields skipped, D-23), once per run, de-duplicated. */
  onWarnings?: (warnings: string[]) => void;
  /** Once per run, after a platen cell's run stopped at a failed commit. */
  onPlatenCommitFailed?: (message: string) => void;
  /**
   * 88a.1 (D4): the drop set a not-set-up store SKU product up (or tried to).
   * Called once after the run settles, when any commit carried `setUp`, with
   * the drop's answers combined (`combineSetUps`). Per-SKU success handling is
   * unchanged.
   */
  onSetUp?: (setUp: ColourUploadSetUp) => void;
  /**
   * BLU-1238 (R3-14): what the commits said about the products' skip platen
   * marks (cleared, sizes still missing, or not set up yet). Called once after
   * the run settles, only when a commit carried any.
   */
  onPlatenSkip?: (outcomes: ColourUploadPlatenSkip[]) => void;
  /**
   * Round 4 (R4-3): once a run (an upload or a retry) settles with at least
   * one SKU published, the SKUs it published, in order. The By colour grid
   * uses it to make the colour's Small file from a published Large.
   */
  onRunSettled?: (publishedSkus: string[]) => void;
}

/** One product's skip mark answer from a commit (the commit's own answer type). */
export type ColourUploadPlatenSkip = NonNullable<
  RouterOutputs['productSync']['commitVariantArtworkUpload']['platenSkip']
>[number];

/**
 * What a commit says about setting the product up (88a.1, D4): `set_up` with
 * the store SKUs it set up now (0 when another run got there first), the
 * variants still waiting for their file, and the products; or `failed` with
 * why. The file was published either way. The commit's own answer type.
 */
export type ColourUploadSetUp = NonNullable<RouterOutputs['productSync']['commitVariantArtworkUpload']['setUp']>;

/**
 * One answer for a drop that took several commits (a cell over
 * `MAX_GROUP_COMMIT_SKUS` SKUs), so the operator sees one toast, never "Set
 * up N" then "already set up": the store SKUs set up are summed, the variants
 * still waiting are the most any commit saw (each commit counts the whole
 * product), the products are merged, and any failure is the answer. Null when
 * no commit carried one.
 */
export function combineSetUps(setUps: readonly ColourUploadSetUp[]): ColourUploadSetUp | null {
  if (setUps.length === 0) return null;
  const failed = setUps.find((setUp) => setUp.status === 'failed');
  if (failed) return failed;
  let declared = 0;
  let waitingForFile = 0;
  const products = new Set<string>();
  for (const setUp of setUps) {
    if (setUp.status !== 'set_up') continue;
    declared += setUp.declared;
    waitingForFile = Math.max(waitingForFile, setUp.waitingForFile);
    for (const product of setUp.products) products.add(product);
  }
  return { status: 'set_up', declared, waitingForFile, products: [...products] };
}

export interface ColourArtworkUploadApi {
  state: ColourArtworkUploadState;
  /** Number of SKUs that have published this run (or retry). */
  doneCount: number;
  /** SKUs whose last attempt failed — the `retryFailed` scope. */
  failedSkus: string[];
  /** Total SKUs targeted this session (0 before the first drop). */
  total: number;
  /** 0..1 aggregate — completed SKUs + the current SKU's fraction over total. */
  progress: number;
  /** Full flow: validate -> hash once -> mint, PUT and commit once per group of SKUs. */
  upload: (file: File) => Promise<void>;
  /** Re-run ONLY the failed SKUs with the retained File + sha (idempotent). */
  retryFailed: () => Promise<void>;
  /** Clear back to idle and revoke the staged preview URL. */
  reset: () => void;
}

/**
 * Group commits in flight per cell. A real cell is one group (at most
 * `MAX_GROUP_COMMIT_SKUS` SKUs); the bound only matters for a larger one. The
 * same bound as the backend `EXECUTE_CONCURRENCY`.
 */
export const COLOUR_UPLOAD_CONCURRENCY = 3;

/** The most SKUs one commit publishes to; mirrors the server's `MAX_COMMIT_SKUS`. */
export const MAX_GROUP_COMMIT_SKUS = 64;

/** What a group commit answers (the fields this hook reads). */
interface CommitResult {
  warnings?: string[];
  skuResults?: Array<{ sku: string; ok: boolean; error?: string }>;
  setUp?: ColourUploadSetUp;
  platenSkip?: ColourUploadPlatenSkip[];
}

const IDLE_STATE: ColourArtworkUploadState = {
  phase: 'idle',
  statuses: {},
  previewUrl: null,
  fileName: null,
  byteSize: null,
  error: null,
};

function isPngFile(file: File): boolean {
  if (file.type) return file.type === ARTWORK_CONTENT_TYPE;
  return file.name.toLowerCase().endsWith('.png');
}

export function useColourArtworkUpload({
  locationId,
  side,
  skus,
  onAllUploaded,
  expectedPlatenVariantKey,
  onWarnings,
  onPlatenCommitFailed,
  onSetUp,
  onPlatenSkip,
  onRunSettled,
}: UseColourArtworkUploadArgs): ColourArtworkUploadApi {
  const { t } = useTranslation('productSync');
  const mint = trpc.productSync.mintVariantArtworkUploadUrl.useMutation();
  const commit = trpc.productSync.commitVariantArtworkUpload.useMutation();

  const [state, setState] = useState<ColourArtworkUploadState>(IDLE_STATE);

  // Monotonic run id — a stale async result (a second drop, a retry, a reset)
  // must never clobber newer state.
  const runIdRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Retained across a run so `retryFailed` can re-publish the SAME bytes/sha to
  // just the failed SKUs without re-reading the File or re-hashing.
  const lastFileRef = useRef<{ file: File; sha256: string } | null>(null);

  // Live scalars read inside the async loop WITHOUT re-creating the callbacks —
  // the fan-out reads `skus`/`side`/`locationId`/`onAllUploaded` from refs so a
  // parent re-render (new `skus` array identity) can't strand an in-flight run
  // on stale values.
  const skusRef = useRef(skus);
  skusRef.current = skus;
  const sideRef = useRef(side);
  sideRef.current = side;
  const locationIdRef = useRef(locationId);
  locationIdRef.current = locationId;
  const onAllUploadedRef = useRef(onAllUploaded);
  onAllUploadedRef.current = onAllUploaded;
  const expectedPlatenVariantKeyRef = useRef(expectedPlatenVariantKey);
  expectedPlatenVariantKeyRef.current = expectedPlatenVariantKey;
  const onWarningsRef = useRef(onWarnings);
  onWarningsRef.current = onWarnings;
  const onPlatenCommitFailedRef = useRef(onPlatenCommitFailed);
  onPlatenCommitFailedRef.current = onPlatenCommitFailed;
  const onSetUpRef = useRef(onSetUp);
  onSetUpRef.current = onSetUp;
  const onPlatenSkipRef = useRef(onPlatenSkip);
  onPlatenSkipRef.current = onPlatenSkip;
  const onRunSettledRef = useRef(onRunSettled);
  onRunSettledRef.current = onRunSettled;

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      runIdRef.current += 1;
      abortRef.current?.abort();
      revokePreview();
    };
  }, [revokePreview]);

  const serverMessage = useCallback(
    (err: unknown, fallbackKey: string): string => {
      const refusal = uploadRefusalKey(err);
      if (refusal) return t(refusal);
      return err instanceof Error && err.message ? err.message : t(fallbackKey);
    },
    [t],
  );

  /**
   * Publish the file to `targetSkus` as GROUP commits (review 1-S2): the SKUs
   * are split into groups of at most `MAX_GROUP_COMMIT_SKUS` (one group for
   * any real cell), and each group mints ONE object under its first SKU, PUTs
   * the bytes ONCE and commits ONCE with `skus` set, so the server verifies
   * the object once and publishes it to every SKU of the group. The server's
   * per-SKU answer (`skuResults`) decides which SKUs are done and which failed.
   * Groups run at most `COLOUR_UPLOAD_CONCURRENCY` at a time.
   *
   * `baseStatuses` seeds the working map so a retry preserves the SKUs that
   * already published (only the failed ones are reset to pending). All state
   * writes are guarded by the run id.
   *
   * Each group's job catches its own errors, so one failure never cancels the
   * pool. With a platen variant key a refused commit refuses the whole group
   * (the server publishes nothing when any SKU's platen variant changed) and
   * stops the run: no further group starts, and every SKU that never started
   * ends `failed` with that commit's server message.
   */
  const runSkus = useCallback(
    async (
      id: number,
      file: File,
      sha256: string,
      targetSkus: string[],
      controller: AbortController,
      baseStatuses: Record<string, SkuUploadStatus>,
    ) => {
      const working: Record<string, SkuUploadStatus> = { ...baseStatuses };
      for (const sku of targetSkus) working[sku] = { phase: 'pending', progress: 0 };

      // Commit a fresh shallow clone so React always sees a new identity. Every
      // write is dropped if a newer run has started.
      const commitStatuses = (phase?: ColourArtworkPhase) => {
        setState((prev) =>
          runIdRef.current === id
            ? { ...prev, statuses: { ...working }, ...(phase ? { phase } : {}) }
            : prev,
        );
      };
      const setGroup = (group: readonly string[], status: SkuUploadStatus) => {
        for (const sku of group) working[sku] = status;
      };

      commitStatuses('running');

      const loc = locationIdRef.current;
      const activeSide = sideRef.current;
      const platenKey = expectedPlatenVariantKeyRef.current;
      // Per-run stop: set by the first refused commit of a platen cell.
      let stoppedMessage: string | null = null;
      const warnings = new Set<string>();
      // Each commit's set-up answer, combined into one once the run settles.
      const setUps: ColourUploadSetUp[] = [];
      // Each commit's skip mark answers, handed on once the run settles.
      const platenSkips: ColourUploadPlatenSkip[] = [];

      const publishGroup = async (group: string[]): Promise<void> => {
        const primary = group[0]!;
        try {
          if (runIdRef.current !== id) return;
          if (stoppedMessage !== null) {
            setGroup(group, { phase: 'failed', progress: 0, message: stoppedMessage });
            commitStatuses();
            return;
          }

          setGroup(group, { phase: 'uploading', progress: 0 });
          commitStatuses();

          // --- mint ONE object, under the group's first SKU ----------------
          let minted: { uploadUrl: string; key: string; publicUrl: string };
          try {
            minted = await mint.mutateAsync({
              locationId: loc,
              sku: primary,
              side: activeSide,
              sha256,
              byteSize: file.size,
              contentType: ARTWORK_CONTENT_TYPE,
            });
          } catch (err) {
            if (runIdRef.current !== id) return;
            setGroup(group, { phase: 'failed', progress: 0, message: serverMessage(err, 'variantArtwork.errMint') });
            commitStatuses();
            return;
          }
          if (runIdRef.current !== id) return;

          // --- PUT the bytes ONCE (real progress, shared by the group) ------
          try {
            await putWithProgress(
              minted.uploadUrl,
              file,
              ARTWORK_CONTENT_TYPE,
              (fraction) => {
                if (runIdRef.current !== id) return;
                setGroup(group, { phase: 'uploading', progress: fraction });
                commitStatuses();
              },
              controller.signal,
            );
          } catch (err) {
            // A superseding run aborts this XHR; bail without touching state.
            if (err instanceof DOMException && err.name === 'AbortError') return;
            if (runIdRef.current !== id) return;
            setGroup(group, { phase: 'failed', progress: 0, message: t('variantArtwork.errUpload') });
            commitStatuses();
            return;
          }
          if (runIdRef.current !== id) return;

          // --- ONE commit publishes the object to every SKU of the group ----
          setGroup(group, { phase: 'publishing', progress: 1 });
          commitStatuses();
          let result: CommitResult;
          try {
            result = await commit.mutateAsync({
              locationId: loc,
              sku: primary,
              skus: group,
              side: activeSide,
              sha256,
              ...(platenKey ? { expectedPlatenVariantKey: platenKey } : {}),
            });
          } catch (err) {
            if (runIdRef.current !== id) return;
            const message = serverMessage(err, 'variantArtwork.errPublish');
            setGroup(group, { phase: 'failed', progress: 1, message });
            if (platenKey && stoppedMessage === null) stoppedMessage = message;
            commitStatuses();
            return;
          }
          if (runIdRef.current !== id) return;
          for (const warning of result.warnings ?? []) warnings.add(warning);
          if (result.setUp) setUps.push(result.setUp);
          if (result.platenSkip) platenSkips.push(...result.platenSkip);

          // The server's per-SKU answer. A server that answered without
          // `skuResults` published only the SKU the object was minted under.
          const answers = new Map((result.skuResults ?? []).map((r) => [r.sku, r]));
          for (const sku of group) {
            const answer = answers.get(sku);
            const published = answer ? answer.ok : result.skuResults === undefined && sku === primary;
            working[sku] = published
              ? { phase: 'done', progress: 1 }
              : { phase: 'failed', progress: 1, message: answer?.error || t('variantArtwork.errPublish') };
          }
          commitStatuses();
        } catch (err) {
          // A job never rejects: an unexpected throw fails this group only.
          if (runIdRef.current !== id) return;
          setGroup(group, { phase: 'failed', progress: 0, message: serverMessage(err, 'variantArtwork.errPublish') });
          commitStatuses();
        }
      };

      const groups: string[][] = [];
      for (let i = 0; i < targetSkus.length; i += MAX_GROUP_COMMIT_SKUS) {
        groups.push(targetSkus.slice(i, i + MAX_GROUP_COMMIT_SKUS));
      }
      await runWithConcurrency(
        groups.map((group) => () => publishGroup(group)),
        COLOUR_UPLOAD_CONCURRENCY,
      );

      if (runIdRef.current !== id) return;

      // Decide the rollup phase from the LOCAL working map (never a stale state
      // read). All done -> success; any failure -> partial (retryable).
      const vals = Object.values(working);
      const anyFailed = vals.some((s) => s.phase === 'failed');
      const allDone = vals.length > 0 && vals.every((s) => s.phase === 'done');
      setState((prev) =>
        runIdRef.current === id ? { ...prev, phase: allDone ? 'success' : anyFailed ? 'partial' : 'success' } : prev,
      );
      if (warnings.size > 0) onWarningsRef.current?.([...warnings]);
      if (stoppedMessage !== null) onPlatenCommitFailedRef.current?.(stoppedMessage);
      const setUp = combineSetUps(setUps);
      if (setUp) onSetUpRef.current?.(setUp);
      if (platenSkips.length > 0) onPlatenSkipRef.current?.(platenSkips);
      const published = targetSkus.filter((sku) => working[sku]?.phase === 'done');
      if (published.length > 0) onRunSettledRef.current?.(published);
      if (allDone) onAllUploadedRef.current?.();
    },
    [commit, mint, serverMessage, t],
  );

  const failValidation = useCallback(
    (kind: ArtworkUploadErrorKind, messageKey: string) => {
      // A validation reject fires BEFORE any run is staged — cancel any in-flight
      // run, drop the staged preview, and surface a hard error. The grid keeps
      // the server-published resting state; this hook only owns the live run, so
      // clearing `statuses` here never wipes an already-published colour.
      runIdRef.current += 1;
      abortRef.current?.abort();
      revokePreview();
      setState({
        phase: 'error',
        statuses: {},
        previewUrl: null,
        fileName: null,
        byteSize: null,
        error: { kind, message: t(messageKey) },
      });
    },
    [revokePreview, t],
  );

  const upload = useCallback(
    async (file: File) => {
      // --- client-side validation (no bytes sent) --------------------------
      if (!isPngFile(file)) return failValidation('invalid_type', 'variantArtwork.errInvalidType');
      if (file.size === 0) return failValidation('empty_file', 'variantArtwork.errEmpty');
      if (file.size > MAX_ARTWORK_BYTES) return failValidation('too_large', 'variantArtwork.errTooLarge');
      if (!window.crypto?.subtle) return failValidation('hash_unavailable', 'variantArtwork.errHashUnavailable');

      const targetSkus = skusRef.current;
      if (targetSkus.length === 0) return;

      // --- stage preview + start a new run ---------------------------------
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const id = (runIdRef.current += 1);

      revokePreview();
      const previewUrl = URL.createObjectURL(file);
      previewUrlRef.current = previewUrl;
      lastFileRef.current = null;

      const pending: Record<string, SkuUploadStatus> = {};
      for (const sku of targetSkus) pending[sku] = { phase: 'pending', progress: 0 };
      setState({
        phase: 'hashing',
        statuses: pending,
        previewUrl,
        fileName: file.name,
        byteSize: file.size,
        error: null,
      });

      // --- hash ONCE -------------------------------------------------------
      let sha256: string;
      try {
        sha256 = await sha256Hex(file);
      } catch {
        if (runIdRef.current !== id) return;
        return failValidation('hash_unavailable', 'variantArtwork.errHashUnavailable');
      }
      if (runIdRef.current !== id) return;
      lastFileRef.current = { file, sha256 };

      await runSkus(id, file, sha256, targetSkus, controller, pending);
    },
    [failValidation, revokePreview, runSkus],
  );

  const retryFailed = useCallback(async () => {
    const last = lastFileRef.current;
    if (!last) return;
    // Read the CURRENT failed set from state at call time.
    const failed = Object.entries(state.statuses)
      .filter(([, s]) => s.phase === 'failed')
      .map(([sku]) => sku);
    if (failed.length === 0) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const id = (runIdRef.current += 1);

    // Seed with the existing statuses so already-published SKUs stay `done`;
    // `runSkus` resets just the retried SKUs to pending.
    await runSkus(id, last.file, last.sha256, failed, controller, state.statuses);
  }, [runSkus, state.statuses]);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    lastFileRef.current = null;
    revokePreview();
    setState(IDLE_STATE);
  }, [revokePreview]);

  // --- derived rollups (from the per-SKU map) ----------------------------
  const { doneCount, failedSkus, progress } = useMemo(() => {
    const pairs = Object.entries(state.statuses);
    const done = pairs.filter(([, s]) => s.phase === 'done').length;
    const failedList = pairs.filter(([, s]) => s.phase === 'failed').map(([sku]) => sku);
    const total = pairs.length;
    // Completed SKUs (weight 1) + each in-flight SKU's fraction, over total.
    const sum = pairs.reduce((acc, [, s]) => acc + (s.phase === 'done' ? 1 : s.progress), 0);
    return { doneCount: done, failedSkus: failedList, progress: total > 0 ? sum / total : 0 };
  }, [state.statuses]);

  return {
    state,
    doneCount,
    failedSkus,
    total: Object.keys(state.statuses).length,
    progress,
    upload,
    retryFailed,
    reset,
  };
}
